import angular from 'angular'

import { clamp, constant } from 'lodash-es'

import SumEmitter from '../utils/sum-emitter'
import embedResize from '../utils/embed-resize'
import viewportResize from '../utils/viewport-resize'

angular
  .module('ggs.gallery-modal')
  .factory('modalLayout', ModalLayoutProvider)

function ModalLayoutProvider() {
  const HOR_LAYOUT = 'hor-layout'
  const VER_LAYOUT = 'ver-layout'

  const VER_MAX_WIDTH = 550
  const TINY_MAX_WIDTH = 800
  const TINY_MAX_HEIGHT = 500

  // bits
  const FOOTER_HEIGHT_CMMN = 42
  const FOOTER_HEIGHT_TINY = 32

  const CLOSE_BUTTON_HEIGHT_CMMN = 110
  const CLOSE_BUTTON_HEIGHT_TINY = 70

  const SWITCH_BUTTONS_WIDTH_CMMN = 80
  const SWITCH_BUTTONS_WIDTH_TINY = 70

  // head height
  const HEAD_HEIGHT_FILE_CMMN = /*user*/40 + /*margins*/30
  const HEAD_HEIGHT_FILE_TINY = /*user*/40 + /*margins*/20

  const HEAD_HEIGHT_TEXT_CMMN = /*user*/40 + /*margin-top-only*/25
  const HEAD_HEIGHT_TEXT_TINY = /*user*/40 + /*margin-top-only*/15

  // hor content
  const HOR_CONTENT_WIDTH_MIN = 50
  const HOR_CONTENT_HEIGHT_MIN = 50

  // ver content
  const VER_CONTENT_WIDTH_MIN = 50
  const VER_CONTENT_HEIGHT_MIN = 50

  // hor pane width
  const HOR_PANE_WIDTH_MAX = 350
  const HOR_PANE_WIDTH_MIN_CMMN = /*vote-button*/160 + /*share-button*/40 + /*margins*/40
  const HOR_PANE_WIDTH_MIN_TINY = /*vote-button*/160 + /*share-button*/40 + /*margins*/20

  // hor pane user height
  const HOR_PANE_USER_HEIGHT_CMMN = /*user*/40 + /*margins*/30
  const HOR_PANE_USER_HEIGHT_TINY = /*user*/40 + /*margins*/15

  // hor pane desc height
  const HOR_PANE_DESC_HEIGHT_MIN_CMMN = 66 /*3 lines*/
  const HOR_PANE_DESC_MARGIN_WITH_NEIGHBORS_CMMN = 0
  const HOR_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_CMMN = 50
  const HOR_PANE_DESC_MARGIN_WITHOUT_TOP_NEIGHBOR_CMMN = 25
  const HOR_PANE_DESC_MARGIN_WITHOUT_BOTTOM_NEIGHBOR_CMMN = 25

  const HOR_PANE_DESC_HEIGHT_MIN_TINY = 44 /*2 lines*/
  const HOR_PANE_DESC_MARGIN_WITH_NEIGHBORS_TINY = 0
  const HOR_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_TINY = 30
  const HOR_PANE_DESC_MARGIN_WITHOUT_TOP_NEIGHBOR_TINY = 15
  const HOR_PANE_DESC_MARGIN_WITHOUT_BOTTOM_NEIGHBOR_TINY = 15

  // hor pane buttons height
  const HOR_PANE_BUTTONS_HEIGHT_CMMN = /*buttons*/40 + /*margins*/30
  const HOR_PANE_BUTTONS_HEIGHT_TINY = /*buttons*/40 + /*margins*/15

  // ver pane width
  const VER_PANE_WIDTH_MIN_CMMN = /*vote-button*/160 + /*share-button*/40 + /*margins*/20
  const VER_PANE_WIDTH_MIN_TINY = /*vote-button*/160 + /*share-button*/40 + /*margins*/10

  // ver pane desc height
  const VER_PANE_DESC_HEIGHT_MIN_CMMN = 66 /*3 lines*/
  const VER_PANE_DESC_MARGIN_WITH_NEIGHBORS_CMMN = 15
  const VER_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_CMMN = 30

  const VER_PANE_DESC_HEIGHT_MIN_TINY = 44 /*2 lines*/
  const VER_PANE_DESC_MARGIN_WITH_NEIGHBORS_TINY = 10
  const VER_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_TINY = 20

  // ver pane buttons height
  const VER_PANE_BUTTONS_HEIGHT_CMMN = 40
  const VER_PANE_BUTTONS_HEIGHT_TINY = 40

  const VER_PANE_BUTTONS_MARGIN_WITH_NEIGHBORS_CMMN = 16
  const VER_PANE_BUTTONS_MARGIN_WITH_NEIGHBORS_TINY = 7

  const VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_FILE_CMMN = 20
  const VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_FILE_TINY = 10

  const VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_TEXT_CMMN = /*margin-bottom-only*/20
  const VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_TEXT_TINY = /*margin-bottom-only*/10

  // bits sizes
  const calcFooterHeight = ({ tiny, attachesFooter }) => !attachesFooter ? 0 : tiny
    ? FOOTER_HEIGHT_TINY
    : FOOTER_HEIGHT_CMMN

  const calcCloseButtonHeight = ({ tiny, showsCloseButton }) => !showsCloseButton ? 0 : tiny
    ? CLOSE_BUTTON_HEIGHT_TINY
    : CLOSE_BUTTON_HEIGHT_CMMN

  const calcSwitchButtonsWidth = ({ tiny, showsSwitchButtons }) => !showsSwitchButtons ? 0 : tiny
    ? SWITCH_BUTTONS_WIDTH_TINY
    : SWITCH_BUTTONS_WIDTH_CMMN

  // head height
  const calcHeadHeight = ({ text, tiny, hasHead }) => !hasHead ? 0 : text
    ? tiny
      ? HEAD_HEIGHT_TEXT_TINY
      : HEAD_HEIGHT_TEXT_CMMN
    : tiny
      ? HEAD_HEIGHT_FILE_TINY
      : HEAD_HEIGHT_FILE_CMMN

  // hor pane width
  const calcHorPaneWidthMax = constant(HOR_PANE_WIDTH_MAX)

  const calcHorPaneWidthMin = ({ tiny }) => tiny
    ? HOR_PANE_WIDTH_MIN_TINY
    : HOR_PANE_WIDTH_MIN_CMMN

  // hor pane height
  const calcHorPaneHeightMin = ({ tiny, hasPaneButtons, hasPaneDesc, hasPaneUser }) => {
    const userHeight = !hasPaneUser ? 0 : tiny
      ? HOR_PANE_USER_HEIGHT_TINY
      : HOR_PANE_USER_HEIGHT_CMMN

    const descHeightMin = !hasPaneDesc ? 0 : tiny
      ? HOR_PANE_DESC_HEIGHT_MIN_TINY
      : HOR_PANE_DESC_HEIGHT_MIN_CMMN

    const descMarginWithNeighbors = !(hasPaneDesc && hasPaneUser && hasPaneButtons) ? 0 : tiny
      ? HOR_PANE_DESC_MARGIN_WITH_NEIGHBORS_TINY
      : HOR_PANE_DESC_MARGIN_WITH_NEIGHBORS_CMMN

    const descMarginWithoutNeighbors = !(hasPaneDesc && !hasPaneUser && !hasPaneButtons) ? 0 : tiny
      ? HOR_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_TINY
      : HOR_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_CMMN

    const descMarginWithoutTopNeighbor = !(hasPaneDesc && !hasPaneUser && hasPaneButtons) ? 0 : tiny
      ? HOR_PANE_DESC_MARGIN_WITHOUT_TOP_NEIGHBOR_TINY
      : HOR_PANE_DESC_MARGIN_WITHOUT_TOP_NEIGHBOR_CMMN

    const descMarginWithoutBottomNeighbor = !(hasPaneDesc && hasPaneUser && !hasPaneButtons) ? 0 : tiny
      ? HOR_PANE_DESC_MARGIN_WITHOUT_BOTTOM_NEIGHBOR_TINY
      : HOR_PANE_DESC_MARGIN_WITHOUT_BOTTOM_NEIGHBOR_CMMN

    const buttonsHeight = !hasPaneButtons ? 0 : tiny
      ? HOR_PANE_BUTTONS_HEIGHT_TINY
      : HOR_PANE_BUTTONS_HEIGHT_CMMN

    return userHeight
      + descHeightMin
      + descMarginWithNeighbors
      + descMarginWithoutNeighbors
      + descMarginWithoutTopNeighbor
      + descMarginWithoutBottomNeighbor
      + buttonsHeight
  }

  // ver pane width
  const calcVerPaneWidthMin = ({ tiny }) => tiny
    ? VER_PANE_WIDTH_MIN_TINY
    : VER_PANE_WIDTH_MIN_CMMN

  // ver pane height
  const calcVerPaneHeightMin = (input) => {
    const descHeightMin = calcVerPaneDescHeightMin(input)
    const descMargin = calcVerPaneDescMargin(input)
    const buttonsHeight = calcVerPaneButtonsHeight(input)
    return descHeightMin + descMargin + buttonsHeight
  }

  const calcVerPaneDescHeightMin = ({ tiny, hasPaneDesc }) => !hasPaneDesc ? 0 : tiny
    ? VER_PANE_DESC_HEIGHT_MIN_TINY
    : VER_PANE_DESC_HEIGHT_MIN_CMMN

  const calcVerPaneDescMargin = ({ tiny, hasPaneDesc, hasPaneButtons }) => {
    const descMarginWithNeighbors = !(hasPaneDesc && hasPaneButtons) ? 0 : tiny
      ? VER_PANE_DESC_MARGIN_WITH_NEIGHBORS_TINY
      : VER_PANE_DESC_MARGIN_WITH_NEIGHBORS_CMMN

    const descMarginWithoutNeighbors = !(hasPaneDesc && !hasPaneButtons) ? 0 : tiny
      ? VER_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_TINY
      : VER_PANE_DESC_MARGIN_WITHOUT_NEIGHBORS_CMMN

    return descMarginWithNeighbors + descMarginWithoutNeighbors
  }

  const calcVerPaneButtonsHeight = ({ text, tiny, hasPaneButtons, hasPaneDesc }) => {
    const buttonsHeight = !hasPaneButtons ? 0 : tiny
      ? VER_PANE_BUTTONS_HEIGHT_TINY
      : VER_PANE_BUTTONS_HEIGHT_CMMN

    const buttonsMarginWithNeighbors = !(hasPaneButtons && hasPaneDesc) ? 0 : tiny
      ? VER_PANE_BUTTONS_MARGIN_WITH_NEIGHBORS_TINY
      : VER_PANE_BUTTONS_MARGIN_WITH_NEIGHBORS_CMMN

    const buttonsMarginWithoutNeighbors = !(hasPaneButtons && !hasPaneDesc) ? 0 : text
      ? tiny
        ? VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_TEXT_TINY
        : VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_TEXT_CMMN
      : tiny
        ? VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_FILE_TINY
        : VER_PANE_BUTTONS_MARGIN_WITHOUT_NEIGHBORS_FILE_CMMN

    return buttonsHeight + buttonsMarginWithNeighbors + buttonsMarginWithoutNeighbors
  }

  // media queries
  const calcHorContentWidthMin = constant(HOR_CONTENT_WIDTH_MIN)
  const calcVerContentWidthMin = (input) => Math.max(calcVerPaneWidthMin(input), VER_CONTENT_WIDTH_MIN)

  const calcHorWidthOffset = (input) => calcSwitchButtonsWidth(input) + extraWidthOffset + input.horPaneWidth
  const calcVerWidthOffset = (input) => calcSwitchButtonsWidth(input) + extraWidthOffset

  const genWidthQuery = (fromWidth, toWidth, softCoef) => ({
    test: (baseWidth) => (baseWidth > fromWidth) && (baseWidth <= toWidth),
    [HOR_LAYOUT]: {
      hardMaxWidth: (baseWidth, input) => Math.max(baseWidth - calcHorWidthOffset(input), calcHorContentWidthMin(input)),
      hardMinWidth: (_baseWidth, input) => calcHorContentWidthMin(input),
      softMaxWidth: (baseWidth, input) => Math.max(softCoef * baseWidth - calcHorWidthOffset(input), calcHorContentWidthMin(input)),
      softMinWidth: (_baseWidth, input) => calcHorContentWidthMin(input)
    },
    [VER_LAYOUT]: {
      hardMaxWidth: (baseWidth, input) => Math.max(baseWidth - calcVerWidthOffset(input), calcVerContentWidthMin(input)),
      hardMinWidth: (_baseWidth, input) => calcVerContentWidthMin(input),
      softMaxWidth: (baseWidth, input) => Math.max(softCoef * baseWidth - calcVerWidthOffset(input), calcVerContentWidthMin(input)),
      softMinWidth: (_baseWidth, input) => calcVerContentWidthMin(input)
    }
  })

  const WIDTH_MQ = [
    genWidthQuery(2300,      Infinity, 10 / 16),
    genWidthQuery(1800,      2300,     12 / 16),
    genWidthQuery(1300,      1800,     13 / 16),
    genWidthQuery(900,       1300,     14 / 16),
    genWidthQuery(550,       900,      15 / 16),
    genWidthQuery(-Infinity, 550,      16 / 16)
  ]

  const calcHorContentHeightMin = (input) => Math.max(calcHorPaneHeightMin(input), HOR_CONTENT_HEIGHT_MIN)
  const calcVerContentHeightMin = constant(VER_CONTENT_HEIGHT_MIN)

  const calcHorHeightOffset = (input) => calcFooterHeight(input) + calcCloseButtonHeight(input) + extraHeightOffset
  const calcVerHeightOffset = (input) => calcFooterHeight(input) + calcCloseButtonHeight(input) + calcHeadHeight(input) + calcVerPaneHeightMin(input) + extraHeightOffset

  const genHeightQuery = (fromHeight, toHeight, softCoef) => ({
    test: (baseHeight) => (baseHeight > fromHeight) && (baseHeight <= toHeight),
    [HOR_LAYOUT]: {
      hardMaxHeight: (baseHeight, input) => Math.max(baseHeight - calcHorHeightOffset(input), calcHorContentHeightMin(input)),
      hardMinHeight: (_baseHeight, input) => calcHorContentHeightMin(input),
      softMaxHeight: (baseHeight, input) => Math.max(softCoef * baseHeight - calcHorHeightOffset(input), calcHorContentHeightMin(input)),
      softMinHeight: (_baseHeight, input) => calcHorContentHeightMin(input)
    },
    [VER_LAYOUT]: {
      hardMaxHeight: (baseHeight, input) => Math.max(baseHeight - calcVerHeightOffset(input), calcVerContentHeightMin(input)),
      hardMinHeight: (_baseHeight, input) => calcVerContentHeightMin(input),
      softMaxHeight: (baseHeight, input) => Math.max(softCoef * baseHeight - calcVerHeightOffset(input), calcVerContentHeightMin(input)),
      softMinHeight: (_baseHeight, input) => calcVerContentHeightMin(input)
    }
  })

  const HEIGHT_MQ = [
    genHeightQuery(2300,      Infinity, 10 / 16),
    genHeightQuery(1800,      2300,     11 / 16),
    genHeightQuery(1300,      1800,     13 / 16),
    genHeightQuery(900,       1300,     14 / 16),
    genHeightQuery(550,       900,      15 / 16),
    genHeightQuery(-Infinity, 550,      16 / 16)
  ]

  // main

  const resizeEmitter = SumEmitter(
    viewportResize,
    embedResize.frameHeight,
    embedResize.viewportHeight)

  function setResizeEmitter(emitter) {
    return resizeEmitter.replace(emitter)
  }

  let altBaseWidthGetter = null

  function setBaseWidthGetter(getter) {
    return altBaseWidthGetter = getter
  }

  let altBaseHeightGetter = null

  function setBaseHeightGetter(getter) {
    return altBaseHeightGetter = getter
  }

  let extraWidthOffset = 0

  function setExtraWidthOffset(offset) {
    return extraWidthOffset = offset
  }

  let extraHeightOffset = 0

  function setExtraHeightOffset(offset) {
    return extraHeightOffset = offset
  }

  function calcName() {
    return getBaseWidth() <= VER_MAX_WIDTH
      ? VER_LAYOUT
      : HOR_LAYOUT
  }

  function calcTiny() {
    return false
      || getBaseWidth() <= TINY_MAX_WIDTH
      || getBaseHeight() <= TINY_MAX_HEIGHT
  }

  function calcHorPaneWidth(tiny) {
    //     (X - x1)
    // Y = -------- * (y2 - y1) + y1
    //     (x2 - x1)
    // X - base width
    // Y - pane width
    // x1 -> y1 - Y should be eq to y1 when X=x1
    // x2 -> y2 - Y should be eq to y2 when X=x2

    const input = { tiny }
    const baseWidth = getBaseWidth()
    const horPaneWidthMax = calcHorPaneWidthMax(input)
    const horPaneWidthMin = calcHorPaneWidthMin(input)

    const a = baseWidth - VER_MAX_WIDTH
    const b = TINY_MAX_WIDTH - VER_MAX_WIDTH
    const c = horPaneWidthMax - horPaneWidthMin
    const d = horPaneWidthMin
    const width = Math.round(a / b * c + d)
    return clamp(width, horPaneWidthMin, horPaneWidthMax)
  }

  function calcVerPaneDescMaxHeight(
    showcaseHeight,
    text,
    tiny,
    attachesFooter,
    showsCloseButton,
    hasHead,
    hasPaneButtons,
    hasPaneDesc) {

    const baseHeight = getBaseHeight()
    const input = {
      text,
      tiny,
      attachesFooter,
      showsCloseButton,
      hasHead,
      hasPaneButtons,
      hasPaneDesc
    }

    const verOffset = calcFooterHeight(input) + calcCloseButtonHeight(input) + calcHeadHeight(input) + calcVerPaneDescMargin(input) + calcVerPaneButtonsHeight(input) + extraHeightOffset
    const rawMaxHeight = Math.round(baseHeight - showcaseHeight - verOffset)
    const normMaxHeight = Math.max(rawMaxHeight, calcVerPaneDescHeightMin(input))
    return normMaxHeight
  }

  function calcShowcaseBounds(
    horPaneWidth,
    name,
    text,
    tiny,
    attachesFooter,
    showsCloseButton,
    showsSwitchButtons,
    hasHead,
    hasPaneButtons,
    hasPaneDesc,
    hasPaneUser) {

    const baseWidth = getBaseWidth()
    const baseHeight = getBaseHeight()

    const input = {
      horPaneWidth,
      text,
      tiny,
      attachesFooter,
      showsCloseButton,
      showsSwitchButtons,
      hasHead,
      hasPaneButtons,
      hasPaneDesc,
      hasPaneUser
    }

    const widthRules = WIDTH_MQ.find((mq) => mq.test(baseWidth))
    const heightRules = HEIGHT_MQ.find((mq) => mq.test(baseHeight))

    const widthRule = widthRules[name]
    const heightRule = heightRules[name]

    const hardMaxWidth = Math.round(widthRule.hardMaxWidth(baseWidth, input))
    const hardMinWidth = Math.round(widthRule.hardMinWidth(baseWidth, input))
    const softMaxWidth = Math.round(widthRule.softMaxWidth(baseWidth, input))
    const softMinWidth = Math.round(widthRule.softMinWidth(baseWidth, input))

    const hardMaxHeight = Math.round(heightRule.hardMaxHeight(baseHeight, input))
    const hardMinHeight = Math.round(heightRule.hardMinHeight(baseHeight, input))
    const softMaxHeight = Math.round(heightRule.softMaxHeight(baseHeight, input))
    const softMinHeight = Math.round(heightRule.softMinHeight(baseHeight, input))

    const normSoftMaxWidth = fixSoftMax(softMaxWidth, hardMaxWidth, hardMinWidth)
    const normSoftMinWidth = fixSoftMin(softMinWidth, hardMaxWidth, hardMinWidth)
    const normSoftMaxHeight = fixSoftMax(softMaxHeight, hardMaxHeight, hardMinHeight)
    const normSoftMinHeight = fixSoftMin(softMinHeight, hardMaxHeight, hardMinHeight)

    return {
      base: {
        width: baseWidth,
        height: baseHeight
      },

      hard: {
        maxWidth: hardMaxWidth,
        minWidth: hardMinWidth,
        maxHeight: hardMaxHeight,
        minHeight: hardMinHeight
      },

      soft: {
        maxWidth: normSoftMaxWidth,
        minWidth: normSoftMinWidth,
        maxHeight: normSoftMaxHeight,
        minHeight: normSoftMinHeight
      }
    }
  }

  function fixSoftMin(softMin, hardMax, hardMin) {
    return (softMin < hardMin || softMin > hardMax) ? hardMin : softMin
  }

  function fixSoftMax(softMax, hardMax, hardMin) {
    return (softMax < hardMin || softMax > hardMax) ? hardMax : softMax
  }

  function getBaseWidth() {
    if (altBaseWidthGetter) {
      return altBaseWidthGetter()
    } else {
      return viewportResize.getWidth()
    }
  }

  function getBaseHeight() {
    const useEmbedViewportHeight = true
      && embedResize.isPositionReady()
      && embedResize.getFrameHeight() > embedResize.getViewportHeight()

    if (altBaseHeightGetter) {
      return altBaseHeightGetter()
    } else if (useEmbedViewportHeight) {
      return embedResize.getViewportHeight()
    } else {
      return viewportResize.getHeight()
    }
  }

  return {
    HOR_LAYOUT,
    VER_LAYOUT,

    resizeEmitter,

    setResizeEmitter,
    setBaseWidthGetter,
    setBaseHeightGetter,
    setExtraWidthOffset,
    setExtraHeightOffset,

    calcTiny,
    calcName,
    calcHorPaneWidth,
    calcShowcaseBounds,
    calcVerPaneDescMaxHeight
  }
}
